import type { ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import type { AgGridContext } from 'ag-grid-community/dist/types/core/interfaces/iCommon';
import { formatInTimeZone } from 'date-fns-tz';
import {
  formattedDate,
  formattedDateWithMilliseconds,
  formattedTime,
  parseDate,
  type DateFnInput,
} from '../../../utils';
import { logger } from '../../../utils/logger';
import { text, type TextColumnParams } from './text';
import type { ColDefFactory, Column } from './types';
import { dateStringComparator } from './utils';

/**
 * IANA timezone string, or a callback receiving (rowData, blotterContext) that returns
 * the timezone string. When supplied, the date is formatted in that timezone and the
 * timezone abbreviation is appended to the output (e.g. "2024-01-15 13:00:00 EST").
 */
export type DateTimezoneParam<TData> =
  | string
  | ((data: TData | undefined, context: AgGridContext) => string | undefined);

export type DateParams<TData extends object = any> = TextColumnParams & {
  milliseconds?: boolean;
  /** Provide a custom formatter to format however you want. Returns the date string the column is pointing at (field), and the timezone if resolvable (given params) */
  customFormatter?: (dateString: DateFnInput | undefined, tz?: string) => string;
  /** When provided, the date is displayed in the specified timezone with the timezone abbreviation appended. */
  timezone?: DateTimezoneParam<TData>;
  /** When set, resolves the cell value instead of the column `field` (same pattern as size/price columns). */
  valueGetter?: (params: ValueGetterParams<TData>) => DateFnInput | undefined;
};

const TIMEZONE_DATE_FORMAT = 'yyyy-MM-dd HH:mm:ss z';
const TIMEZONE_DATE_FORMAT_MILLIS = 'yyyy-MM-dd HH:mm:ss.SSS z';

function resolveTimezone<TData>(
  timezone: DateTimezoneParam<TData> | undefined,
  data: TData,
  context: AgGridContext
): string | undefined {
  if (typeof timezone === 'function') {
    return timezone(data, context);
  }
  return timezone;
}

/** Date column with configurable formatting and milliseconds support */
export const date: ColDefFactory<Column<DateParams>> = column => ({
  ...text(column),
  ...(column.params?.valueGetter ? { valueGetter: column.params.valueGetter } : {}),
  cellDataType: 'dateString',
  initialWidth: column.params?.milliseconds ? 200 : 160,
  comparator: dateStringComparator,
  valueFormatter: ({ value, data, context }: ValueFormatterParams<unknown, DateFnInput>) => {
    if (!value || value === '0') {
      return '';
    }
    try {
      const tz = resolveTimezone(column.params?.timezone, data, context);
      if (column.params?.timezone && !tz) {
        return ''; // if the implementer wants us to render in some tz, but we cant find one, render empty
      }

      if (column.params?.customFormatter) {
        return column.params.customFormatter(value, tz);
      }

      if (tz) {
        return formatInTimeZone(
          parseDate(value),
          tz,
          column.params?.milliseconds ? TIMEZONE_DATE_FORMAT_MILLIS : TIMEZONE_DATE_FORMAT
        );
      }

      return (column.params?.milliseconds ? formattedDateWithMilliseconds(value) : formattedDate(value)) ?? '';
    } catch (e) {
      return '';
    }
  },
  sortingOrder: ['desc', 'asc'],
});

/** Time column showing UTC time */
export const time: ColDefFactory<Column> = column => ({
  ...text(column),
  cellDataType: 'dateString',
  initialWidth: 75,
  comparator: dateStringComparator,
  valueFormatter: ({ value }: ValueFormatterParams<unknown, DateFnInput>) => {
    if (!value || value === '0') {
      return '';
    }
    try {
      return `${formattedTime(value)} UTC`;
    } catch (e) {
      logger.error(e as Error);
      return '*Invalid Time*';
    }
  },
  headerClass: 'ag-right-aligned-cell',
  type: 'rightAligned',
  sortingOrder: ['desc', 'asc'],
});
